import React from 'react';
import { X, FileText, Zap, Activity, Tag, Users, ExternalLink, Share2, Sun } from 'lucide-react';
import { useGetSiteEventQuery } from '../../redux/api/eventsApi';
import { formatDateTime } from '../../utils/dateUtils';
import { SEVERITY_PANEL_COLORS } from '../../constants/eventStyles';
import { useComponentPowerMap, getTotalStcPowerKw } from '../../hooks/useComponentPowerMap';

interface EventDetailPanelProps {
  siteId: string;
  eventId: string;
  onClose: () => void;
}

const EventDetailPanel: React.FC<EventDetailPanelProps> = ({ siteId, eventId, onClose }) => {
  const { data: event, isLoading, error } = useGetSiteEventQuery({ siteId, eventId });
  const { powerMap } = useComponentPowerMap();

  const connectedPowerKw = event?.affected_entities
    ? getTotalStcPowerKw(event.affected_entities.map((e) => e.entity_identifier), powerMap)
    : null;

  return (
    <div
      className="relative h-full border-l border-[var(--color-border)] transition-all duration-300 ease-in-out flex flex-col z-20 w-[420px]"
      style={{
        background: 'var(--color-bg-primary)',
        backdropFilter: 'blur(16px) saturate(120%)',
        WebkitBackdropFilter: 'blur(16px) saturate(120%)',
        boxShadow: '-4px 0 32px 0 rgba(0, 0, 0, 0.4)',
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)]">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)] truncate">Event Detail</h2>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:bg-[var(--color-surface-hover)] text-[var(--color-text-tertiary)] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5 bg-[var(--color-bg-primary)]">
        {isLoading && (
          <div className="flex items-center justify-center py-12 text-[var(--color-text-muted)]">
            Loading...
          </div>
        )}

        {error && (
          <div className="text-sm text-red-500 rounded-lg p-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)' }}>
            Failed to load event details.
          </div>
        )}

        {event && (
          <>
            <div>
              <div className="flex items-start gap-2 mb-2">
                <h3 className="text-base font-semibold text-[var(--color-text-primary)] flex-1">
                  {event.title}
                </h3>
                <span className={`shrink-0 px-2 py-0.5 text-xs font-semibold uppercase rounded ${SEVERITY_PANEL_COLORS[event.severity].bg} ${SEVERITY_PANEL_COLORS[event.severity].text}`}>
                  {event.severity}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
                  {event.event_type}
                </span>
                <span className={`px-1.5 py-0.5 rounded font-medium ${
                  event.status === 'resolved' || event.status === 'closed'
                    ? 'bg-emerald-500/15 text-emerald-500'
                    : event.status === 'in_progress'
                      ? 'bg-blue-500/15 text-blue-500'
                      : 'bg-gray-500/15 text-gray-500'
                }`}>
                  {event.status.replace('_', ' ')}
                </span>
              </div>
            </div>

            {event.description && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-1">Description</h4>
                <p className="text-sm text-[var(--color-text-primary)] leading-relaxed">{event.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-0.5">Occurred</h4>
                <p className="text-sm text-[var(--color-text-primary)]">{formatDateTime(event.occurred_at)}</p>
              </div>
              {event.resolved_at && (
                <div>
                  <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-0.5">Resolved</h4>
                  <p className="text-sm text-[var(--color-text-primary)]">{formatDateTime(event.resolved_at)}</p>
                </div>
              )}
            </div>

            {(event.energy_impact_kwh != null || event.capacity_affected_kw != null || connectedPowerKw != null) && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-2">Impact</h4>
                <div className="grid grid-cols-2 gap-3">
                  {connectedPowerKw != null && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-tertiary)]">
                      <Sun className="w-4 h-4 text-[#17c480]" />
                      <div>
                        <p className="text-xs text-[var(--color-text-muted)]">Connected STC</p>
                        <p className="text-sm font-medium text-[var(--color-text-primary)]">{connectedPowerKw.toLocaleString(undefined, { maximumFractionDigits: 1 })} kW</p>
                      </div>
                    </div>
                  )}
                  {event.energy_impact_kwh != null && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-tertiary)]">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <div>
                        <p className="text-xs text-[var(--color-text-muted)]">Energy Impact</p>
                        <p className="text-sm font-medium text-[var(--color-text-primary)]">{event.energy_impact_kwh.toLocaleString()} kWh</p>
                      </div>
                    </div>
                  )}
                  {event.capacity_affected_kw != null && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-tertiary)]">
                      <Activity className="w-4 h-4 text-orange-500" />
                      <div>
                        <p className="text-xs text-[var(--color-text-muted)]">Capacity</p>
                        <p className="text-sm font-medium text-[var(--color-text-primary)]">{event.capacity_affected_kw.toLocaleString()} kW</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {event.theme && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-1">Theme</h4>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-tertiary)]">
                  <Tag className="w-4 h-4 text-[#17c480]" />
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-primary)]">{event.theme.name}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      {event.theme.category}
                      {event.theme_confidence != null && ` · ${Math.round(event.theme_confidence * 100)}% confidence`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {event.affected_entities && event.affected_entities.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-2">
                  Affected Components ({event.affected_entities.length})
                </h4>
                <div className="space-y-1.5">
                  {event.affected_entities.map((entity) => (
                    <div key={entity.id} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-tertiary)]">
                      <Users className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
                      <span className="text-sm text-[var(--color-text-primary)]">{entity.entity_identifier}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-surface)] text-[var(--color-text-muted)]">
                        {entity.entity_type}
                      </span>
                      <span className="text-[10px] text-[var(--color-text-muted)] ml-auto">{entity.role}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'networkviewer' } }));
                          window.dispatchEvent(new CustomEvent('navigateToNetworkNode', {
                            detail: { entityIdentifiers: [entity.entity_identifier], siteId }
                          }));
                        }}
                        className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-[#17c480] transition-colors"
                        title="View in Network"
                      >
                        <Share2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    const identifiers = event.affected_entities!.map((e: any) => e.entity_identifier);
                    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'networkviewer' } }));
                    window.dispatchEvent(new CustomEvent('navigateToNetworkNode', {
                      detail: { entityIdentifiers: identifiers, siteId }
                    }));
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 text-xs font-medium rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-tertiary)] hover:bg-[var(--color-surface-hover)] hover:border-[#17c480]/40 transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5 text-[#17c480]" />
                  <span className="text-[var(--color-text-primary)]">View in Network</span>
                  <ExternalLink className="w-3 h-3 text-[var(--color-text-muted)] ml-auto" />
                </button>
              </div>
            )}

            {event.extraction_confidence != null && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-1">Extraction Confidence</h4>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-[var(--color-bg-muted)]">
                    <div
                      className="h-full rounded-full bg-[#17c480]"
                      style={{ width: `${Math.round(event.extraction_confidence * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                    {Math.round(event.extraction_confidence * 100)}%
                  </span>
                </div>
              </div>
            )}

            {(event.document_id || event.meta?.source_page != null || event.meta?.source_excerpt) && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-1">Source Document</h4>
                {event.document_id && (
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-medium rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-tertiary)] hover:bg-[var(--color-surface-hover)] transition-colors"
                    onClick={() => {
                      const sourcePage = event.meta?.source_page as number | undefined;
                      const sourceExcerpt = event.meta?.source_excerpt as string | undefined;
                      const navDetail = {
                        documentId: event.document_id,
                        artefactId: event.document_id,
                        sourceLocation: sourcePage != null ? {
                          page_number: sourcePage,
                          excerpt: sourceExcerpt || null,
                        } : null,
                      };
                      window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'dataroom' } }));
                      window.dispatchEvent(new CustomEvent('openDocumentDetail', { detail: navDetail }));
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('openDocumentDetail', { detail: navDetail }));
                      }, 500);
                    }}
                  >
                    <FileText className="w-3.5 h-3.5 shrink-0 text-[var(--color-text-muted)]" />
                    <span className="flex-1 truncate text-[var(--color-text-primary)]">
                      View Source
                      {event.meta?.source_page != null && ` — Page ${event.meta.source_page}`}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 shrink-0 text-[var(--color-text-muted)]" />
                  </button>
                )}
                {event.meta?.source_excerpt && (
                  <div className="mt-2 p-2.5 rounded-lg bg-[var(--color-bg-tertiary)] border border-[var(--color-border)]">
                    {event.meta.source_page != null && !event.document_id && (
                      <p className="text-xs font-medium text-[var(--color-text-primary)] mb-1">
                        Page {event.meta.source_page}
                      </p>
                    )}
                    <blockquote className="text-xs text-[var(--color-text-tertiary)] italic border-l-2 border-[#17c480] pl-2 line-clamp-4">
                      "{event.meta.source_excerpt}"
                    </blockquote>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventDetailPanel;
